import {
  ContentBlocks_ImageVideo,
  ContentBlocks_TextBlock,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/content_blocks.types';
import {ProductTileV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_v2.types';
import {PromotionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useTrackPromotion} from '@aboutyou/tracking';
import {
  HeroElementSliderProductTile,
  HorizontalSlider,
  SliderWrapper,
  tabletAndUp,
  tabletAndUpLinaria,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React, {useCallback, useState} from 'react';
import sc, {css} from 'styled-components';
import {ProductTile} from '../../../ProductTile/ProductTile';
import {HeroElementCollapsibleText} from './HeroElementCollapsibleText';
import {
  HeroElementHeadline,
  ImageOrVideoTile,
  VideoBlur,
  hexToRgb,
} from './shared';

interface HeroElementWrapperProps {
  headline?: ContentBlocks_TextBlock;
  text?: ContentBlocks_TextBlock;
  media: ContentBlocks_ImageVideo;
  productTiles: ProductTileV2[];
  backgroundColor: string;
  layout: 'desktop' | 'mobile';
  promotionTracker: PromotionTracker;
  streamPosition: number;
  uniqueKey: string;
}

const MOBILE_SLIDE_COUNT = 2.5;

const TextSection: React.FC<
  Pick<HeroElementWrapperProps, 'text' | 'headline'> & {
    isOpen: boolean;
    onToggle: (isOpen: boolean) => void;
  }
> = ({headline, text, isOpen, onToggle}) => {
  return (
    <TextWrapper $isOpen={isOpen}>
      <div>
        {headline && (
          <HeroElementHeadline color="White">
            {headline.text}
          </HeroElementHeadline>
        )}

        {text && (
          <HeroElementCollapsibleText
            text={text.text}
            isOpen={isOpen}
            onToggle={onToggle}
          />
        )}
      </div>
    </TextWrapper>
  );
};

export const HeroElementSlider: React.FC<HeroElementWrapperProps> = ({
  headline,
  text,
  productTiles,
  media,
  backgroundColor,
  layout,
  promotionTracker,
  streamPosition,
  uniqueKey,
}) => {
  const [accordionOpen, setAccordionOpen] = useState(false);
  const trackPromotion = useTrackPromotion();

  const {ref} = useInViewEffect<HTMLLIElement>(
    useCallback(inView => {
      if (inView) {
        trackPromotion({
          promotion: {
            source: TrackingPromotionCreative.Teaser,
            heroElement: {...promotionTracker},
          },
          recoList: {
            sectionIndex: 0,
            recoType: 'brands_hero_product_tile',
            elements: [],
          },
          streamPosition,
          actionType: 'impression',
        });
      }
    }, []),
    {triggerOnce: true, threshold: 0.5},
  );

  return (
    <Wrapper ref={ref}>
      <ContentWrapper>
        <ImageOrVideoTile
          media={media}
          videoControlsPosition={layout === 'mobile' ? 'top' : 'left-top'}
        />

        {accordionOpen && <VideoBlur />}

        <BodyWrapper $gradientColor={hexToRgb(backgroundColor)}>
          {layout === 'mobile' && (
            <>
              <TextSection
                text={text}
                headline={headline}
                isOpen={accordionOpen}
                onToggle={isOpen => setAccordionOpen(isOpen)}
              />
              <StyledHorizontalSlider
                slideCount={MOBILE_SLIDE_COUNT}
                hideSideButtons
                disableHeaderControl
                uniqueKey={uniqueKey}
              >
                {productTiles.map((productTile, index) => {
                  return (
                    <ProductTile
                      gridColumns={Math.floor(MOBILE_SLIDE_COUNT)}
                      key={productTile.productId}
                      tile={productTile}
                      streamPosition={index}
                      ProductTileComponent={HeroElementSliderProductTile}
                      trackingData={{
                        recoType: 'brands_hero_product_tile',
                        promotionId: promotionTracker.promotionId,
                        creativeName: promotionTracker.creativeName,
                      }}
                    />
                  );
                })}
              </StyledHorizontalSlider>
            </>
          )}
        </BodyWrapper>
        {layout === 'desktop' && (
          <AbsoluteContent>
            <TextSection
              text={text}
              headline={headline}
              isOpen={accordionOpen}
              onToggle={isOpen => setAccordionOpen(isOpen)}
            />
            <StyledHorizontalSlider
              constrained
              moveSideNavTop
              slideCount={2.28}
              slideCountLarge={2.1}
              slideCountExtraLarge={2.05}
              disableHeaderControl
              uniqueKey={uniqueKey}
            >
              {productTiles.map((productTile, index) => {
                return (
                  <ProductTile
                    key={productTile.productId}
                    tile={productTile}
                    streamPosition={index}
                    ProductTileComponent={HeroElementSliderProductTile}
                    trackingData={{
                      recoType: 'brands_hero_product_tile',
                      promotionId: promotionTracker.promotionId,
                      creativeName: promotionTracker.creativeName,
                    }}
                    // hardcoded to two as Hero Elements are loaded later in execution and will not impact LCP
                    // also, larger images are a lot nicer for look at and feels better then smaller images on promotion items
                    // images are heavly cached so pulling larger images has no impact
                    gridColumns={2}
                  />
                );
              })}
            </StyledHorizontalSlider>
          </AbsoluteContent>
        )}
      </ContentWrapper>
    </Wrapper>
  );
};

const Wrapper = sc.li`
  padding-top: 1rem;
  padding-bottom: 3rem;
`;

const TextWrapper = sc.div<{$isOpen?: boolean}>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding: 2rem 1.5rem 0;
  gap: 0.25rem;

  backdrop-filter: blur(1px);
  mask-composite: intersect;
  mask: ${({$isOpen}) =>
    `linear-gradient(to top, #000000 0%, #000000 ${
      $isOpen ? '100%' : '130px'
    } 0, transparent 50%);`};

  & > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  ${tabletAndUp(css`
    backdrop-filter: blur(2px);

    padding: 0.5rem 3.75rem 3rem 3.75rem;
    width: 75%;
    text-align: center;
  `)}
`;

const ContentWrapper = sc.div`
  position: relative;
  display: flex;
  margin: 0 -16px 14rem;

  ${tabletAndUp(css`
    flex-direction: row;
    margin: 0;
    border-radius: 0.25rem;
    overflow: hidden;
  `)}
`;
const StyledHorizontalSlider = linariaStyled(HorizontalSlider)`
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  pointer-events: auto;
  ${SliderWrapper} {
    height: auto;
  }
  ${tabletAndUpLinaria(`
    margin-top: 0;
    margin-bottom: 0;
  `)}
`;

const AbsoluteContent = linariaStyled.div`
  position: absolute;
  display: flex;
  height: 100%;
  width: 100%;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.3) 0%, transparent 100%);

  ${StyledHorizontalSlider} {
    padding-top: 1.5rem;
  }
`;

const BodyWrapper = sc.div<{
  $gradientColor: {
    r: number;
    g: number;
    b: number;
  };
}>`
  width: 100%;
  pointer-events: auto;
  position: absolute;
  bottom: -14rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  ${props => {
    const {r, g, b} = props.$gradientColor;
    return css`
      background-image: linear-gradient(
        to top,
        rgba(${r}, ${g}, ${b}, 1) 60%,
        rgba(${r}, ${g}, ${b}, 0) 100%
      );
      ${tabletAndUp(css`
        background-color: rgba(${r}, ${g}, ${b}, 1);
        :before {
          content: '';
          position: absolute;
          width: 30%;
          height: 100%;
          top: 0;
          left: -30%;
          background-image: linear-gradient(
            to left,
            rgba(${r}, ${g}, ${b}, 1) 0%,
            rgba(${r}, ${g}, ${b}, 0) 100%
          );
        }
      `)}
    `;
  }}
  ${tabletAndUp(css`
    gap: 0.5rem;

    position: inherit;
    width: 100%;
    bottom: auto;
    align-items: flex-start;
    padding-top: 1.5rem;
  `)}
`;
